import { useState, useEffect, useCallback } from 'react';
import { Clock, Sparkles, Check, RefreshCw, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API, ScoreBadge } from './constants';

export function CrmRelances({ onRefresh }) {
  const [relances, setRelances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [generatedMsg, setGeneratedMsg] = useState({});

  const fetchRelances = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/crm/relances`);
      const data = await res.json();
      setRelances(data);
    } catch { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchRelances(); }, [fetchRelances]);

  const generateRelance = async (prospect) => {
    setGenerating(prospect.id);
    try {
      const res = await fetch(`${API}/api/crm/generate-rapide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nom: prospect.contact || prospect.nom,
          entreprise: prospect.nom,
          segment: prospect.secteur || 'entreprise',
          sequence: prospect.sequence_suggeree,
          canal: 'email',
          prospect_id: prospect.id,
          contexte: prospect.note || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setGeneratedMsg(prev => ({ ...prev, [prospect.id]: data.message }));
      toast.success(`Message ${prospect.sequence_suggeree} genere`);
    } catch (e) {
      toast.error(e.message || 'Erreur');
    }
    setGenerating(null);
  };

  const copyMsg = (id) => {
    navigator.clipboard.writeText(generatedMsg[id]);
    toast.success('Copie !');
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-4" data-testid="crm-relances">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-amber-500" />
          <h3 className="text-sm font-bold text-gray-700">Relances intelligentes ({relances.length})</h3>
        </div>
        <button onClick={fetchRelances} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
          <RefreshCw size={12} /> Actualiser
        </button>
      </div>

      {relances.length === 0 ? (
        <div className="text-center py-12">
          <Check size={32} className="mx-auto text-green-400 mb-3" />
          <p className="text-sm text-gray-500 font-medium">Tout est a jour !</p>
          <p className="text-xs text-gray-400 mt-1">Aucun prospect ne necessite de relance pour le moment</p>
        </div>
      ) : (
        <div className="space-y-2">
          {relances.map(r => (
            <div key={r.id} className="bg-white rounded-xl border hover:border-[#009893]/30 p-3 transition-all" data-testid={`relance-${r.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-700 truncate">{r.nom}</p>
                    <ScoreBadge score={r.scoreIA} />
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{r.raison}</p>
                </div>
                <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  r.sequence_suggeree === 'J+30' ? 'bg-red-100 text-red-600' :
                  r.sequence_suggeree === 'J+12' ? 'bg-orange-100 text-orange-600' :
                  r.sequence_suggeree === 'J+5' ? 'bg-amber-100 text-amber-600' :
                  'bg-blue-100 text-blue-600'
                }`}>{r.sequence_suggeree}</span>
              </div>

              {generatedMsg[r.id] ? (
                <div className="bg-[#f8fffe] border border-[#c0e8e7] rounded-lg p-3 mt-2">
                  <pre className="text-xs text-gray-600 whitespace-pre-wrap font-sans leading-relaxed">{generatedMsg[r.id]}</pre>
                  <button onClick={() => copyMsg(r.id)} className="mt-2 px-3 py-1 bg-[#009893] text-white text-xs rounded-lg flex items-center gap-1 hover:bg-[#008580]" data-testid={`copy-relance-${r.id}`}>
                    <Copy size={10} /> Copier
                  </button>
                </div>
              ) : (
                <button onClick={() => generateRelance(r)} disabled={generating === r.id}
                  className="w-full mt-2 py-2 rounded-lg bg-[#009893]/10 text-[#009893] text-xs font-medium hover:bg-[#009893]/20 flex items-center justify-center gap-1 disabled:opacity-50 transition-all"
                  data-testid={`gen-relance-${r.id}`}>
                  {generating === r.id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  {generating === r.id ? 'Generation...' : `Generer relance ${r.sequence_suggeree}`}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
